/**
 * DeploymentReadinessBanner — iter78 → iter84.56.3
 *
 * Pre-deploy self-test for Platform Admins. Calls
 * GET /api/site-admin/deploy-readiness and shows a single status badge
 * (green / amber / red) with a collapsible per-check detail list.
 *
 * iter84.56.3 — Migrated to the unified CollapsibleCard shell. The
 * headline + Re-check button moved into the body so the header strip
 * stays compact and consistent with siblings. Auto-opens on fail so
 * the red signal is never hidden behind a collapsed shell.
 *
 * Goal: give the human operator a single "is the preview safe to redeploy?"
 * signal without leaving the admin UI.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    CheckCircle2, AlertTriangle, XCircle, RefreshCw, Rocket, Info, ClipboardCopy, Camera, X,
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const STATUS_META = {
    ok:   { Icon: CheckCircle2, color: "text-emerald-400", border: "border-emerald-500/40", label: "PASS" },
    warn: { Icon: AlertTriangle, color: "text-amber-400",  border: "border-amber-500/40",  label: "WARN" },
    fail: { Icon: XCircle,       color: "text-rose-400",   border: "border-rose-500/40",   label: "FAIL" },
};

// iter84.56.10 — Protection Category criticality vocabulary. Maps the
// backend's `criticality` string to a pill color + label so each
// category header tells the operator "how bad is it if this fails?".
const CRITICALITY_META = {
    critical:    { color: "text-rose-300",    border: "border-rose-500/50",    label: "CRITICAL" },
    important:   { color: "text-amber-300",   border: "border-amber-500/50",   label: "IMPORTANT" },
    operational: { color: "text-cyan-300",    border: "border-cyan-500/50",    label: "OPERATIONAL" },
    trust:       { color: "text-emerald-300", border: "border-emerald-500/50", label: "TRUST" },
};

function CheckRow({ check, inSnapshot, snapshotStatus }) {
    const meta = STATUS_META[check.status] || STATUS_META.warn;
    const { Icon } = meta;
    // iter84.57.10 — When this row was captured in the diagnostic
    // snapshot the operator pasted, highlight it with a left accent +
    // camera pill so the agent (or another human) can spot at a glance
    // which signals motivated the snapshot. Color reflects the SNAPSHOT
    // status, not the current live status — so a row that has since
    // self-healed (live=ok, snapshot=fail) still surfaces the past pain.
    const snapMeta = snapshotStatus ? (STATUS_META[snapshotStatus] || STATUS_META.warn) : null;
    return (
        <li
            data-testid={`deploy-readiness-check-${check.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
            data-signal-key={check.key || ""}
            data-in-snapshot={inSnapshot ? "1" : "0"}
            className={`flex items-start gap-2 py-1.5 text-xs ${inSnapshot ? `pl-2 border-l-2 ${snapMeta?.border || "border-amber-500/40"}` : ""}`}
        >
            <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.color}`} />
            <div className="flex-1 min-w-0">
                <span className="font-bold text-white">{check.name}</span>
                {inSnapshot && (
                    <span
                        className={`inline-flex items-center gap-0.5 ml-1.5 align-middle pill border text-[9px] font-bold uppercase py-0 px-1 ${snapMeta?.color || "text-amber-300"} ${snapMeta?.border || "border-amber-500/40"}`}
                        title={`In diagnostic snapshot (was ${snapshotStatus?.toUpperCase()})`}
                        data-testid={`deploy-readiness-check-in-snapshot-${check.key || ""}`}
                    >
                        <Camera className="w-2.5 h-2.5" /> SNAP
                    </span>
                )}
                {check.tooltip && (
                    <Tooltip delayDuration={150}>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-label={`What does "${check.name}" protect against?`}
                                data-testid={`deploy-readiness-tooltip-trigger-${check.key || check.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="inline-flex items-center align-middle ml-1 text-zinc-500 hover:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 rounded-sm transition-colors"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Info className="w-3 h-3" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            align="start"
                            sideOffset={6}
                            className="max-w-xs bg-zinc-900 border border-zinc-700 text-zinc-200 text-[11px] leading-relaxed font-normal normal-case p-2.5"
                            data-testid={`deploy-readiness-tooltip-${check.key || ""}`}
                        >
                            {check.tooltip}
                        </TooltipContent>
                    </Tooltip>
                )}
                {check.detail && <span className="text-zinc-400 ml-2">— {check.detail}</span>}
            </div>
            <span className={`pill border text-[10px] font-bold uppercase py-0.5 px-1.5 ${meta.color} ${meta.border}`}>
                {meta.label}
            </span>
        </li>
    );
}

// Map deploy-readiness `overall` to CollapsibleCard's verdict vocabulary.
function overallToVerdict(overall) {
    if (overall === "ok") return "clear";
    if (overall === "warn") return "warn";
    if (overall === "fail") return "fail";
    return "info";
}

const VERDICT_PILL = {
    ok:   "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    warn: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    fail: "bg-rose-500/15 text-rose-400 border-rose-500/40",
};

// iter84.57.10 — Decode the `?diag=<base64>` snapshot the operator (or
// another agent) pasted into the URL when copying a diagnostic from
// the "Copy diagnostic" button (Banner ribbon, iter84.57.2). Mirror
// of the encoder at line ~220:
//   const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
//     .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
// On decode we reverse the url-safe substitutions, re-pad, then
// utf-8-decode. Defensive: any failure returns null so a malformed URL
// can't crash the banner — the worst case is the snapshot ribbon
// simply doesn't render.
function decodeDiagSnapshot(searchString) {
    try {
        const params = new URLSearchParams(searchString || "");
        const raw = params.get("diag");
        if (!raw) return null;
        let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
        // Re-pad to a multiple of 4.
        while (b64.length % 4) b64 += "=";
        const json = decodeURIComponent(escape(atob(b64)));
        const parsed = JSON.parse(json);
        // Minimal shape validation — anything else just falls through
        // to "ignore the snapshot".
        if (!parsed || typeof parsed !== "object") return null;
        if (!parsed.overall) return null;
        return {
            ts: parsed.ts || null,
            environment: parsed.environment || "unknown",
            overall: parsed.overall,
            iter: parsed.iter || null,
            warns: Array.isArray(parsed.warns) ? parsed.warns : [],
            fails: Array.isArray(parsed.fails) ? parsed.fails : [],
        };
    } catch (_err) {
        // Bad base64, bad JSON, malformed payload — silently skip.
        return null;
    }
}

// Human-readable relative time for the snapshot ribbon. We deliberately
// keep this trivial (no moment.js / date-fns) — the snapshot ribbon is
// transient context, not a precise timeline view.
function relativeTime(iso) {
    if (!iso) return "unknown time";
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return "unknown time";
    const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86400)}d ago`;
}

// iter84.57.10 — Remove the `?diag=…` param from the URL without
// triggering a navigation. Used by the snapshot ribbon's Clear button
// once the operator has finished comparing snapshot ⇄ live.
function clearDiagFromUrl() {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete("diag");
        const newSearch = url.searchParams.toString();
        const next = `${url.pathname}${newSearch ? "?" + newSearch : ""}${url.hash}`;
        window.history.replaceState({}, "", next);
    } catch (_err) { /* no-op */ }
}

function SnapshotRibbon({ snapshot, liveOverall, liveEnvironment, liveIter, onClear }) {
    const snapMeta = STATUS_META[snapshot.overall] || STATUS_META.warn;
    const envMismatch = snapshot.environment !== "unknown"
        && liveEnvironment
        && snapshot.environment !== liveEnvironment;
    const iterMismatch = snapshot.iter && liveIter && snapshot.iter !== liveIter;
    const verdictDelta = liveOverall && snapshot.overall !== liveOverall;
    const subParts = [];
    if (envMismatch) {
        subParts.push(`env mismatch (snap=${snapshot.environment} · live=${liveEnvironment})`);
    }
    if (iterMismatch) {
        subParts.push(`iter drift (snap=${snapshot.iter} · live=${liveIter})`);
    }
    if (verdictDelta) {
        subParts.push(`verdict changed (snap=${snapshot.overall.toUpperCase()} · live=${liveOverall.toUpperCase()})`);
    }
    if (!subParts.length) {
        subParts.push("snapshot and live state match");
    }
    return (
        <div
            data-testid="deploy-readiness-snapshot-ribbon"
            data-snapshot-overall={snapshot.overall}
            data-env-mismatch={envMismatch ? "1" : "0"}
            data-iter-mismatch={iterMismatch ? "1" : "0"}
            data-verdict-delta={verdictDelta ? "1" : "0"}
            className={`mb-3 px-3 py-2 border-l-4 ${snapMeta.border} bg-zinc-900/60 flex items-start gap-3`}
        >
            <Camera className={`w-4 h-4 mt-0.5 shrink-0 ${snapMeta.color}`} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-[11px] leading-tight">
                    <span className="font-bold text-white uppercase tracking-wider">
                        Diagnostic snapshot
                    </span>
                    <span
                        className={`pill border text-[9px] font-bold uppercase py-0.5 px-1.5 ${snapMeta.color} ${snapMeta.border}`}
                        data-testid="deploy-readiness-snapshot-verdict"
                    >
                        {snapMeta.label}
                    </span>
                    <span className="text-zinc-500">·</span>
                    <span className="text-zinc-300" data-testid="deploy-readiness-snapshot-age">
                        {relativeTime(snapshot.ts)}
                    </span>
                    {snapshot.environment !== "unknown" && (
                        <>
                            <span className="text-zinc-500">·</span>
                            <span className="text-zinc-400 lowercase">{snapshot.environment}</span>
                        </>
                    )}
                    {snapshot.iter && (
                        <>
                            <span className="text-zinc-500">·</span>
                            <span className="text-zinc-400 font-mono">iter{snapshot.iter}</span>
                        </>
                    )}
                </div>
                <p
                    className={`text-[10px] mt-0.5 leading-snug ${(envMismatch || iterMismatch || verdictDelta) ? "text-amber-300" : "text-zinc-500"}`}
                    data-testid="deploy-readiness-snapshot-deltas"
                >
                    {subParts.join(" · ")}
                </p>
                {(snapshot.fails.length + snapshot.warns.length) > 0 && (
                    <p
                        className="text-[10px] text-zinc-500 mt-0.5 leading-snug"
                        data-testid="deploy-readiness-snapshot-summary"
                    >
                        Snapshot captured {snapshot.fails.length} fail · {snapshot.warns.length} warn — highlighted below.
                    </p>
                )}
            </div>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClear(); }}
                data-testid="deploy-readiness-snapshot-clear-btn"
                className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer shrink-0"
                title="Remove ?diag= from the URL and clear the snapshot ribbon."
            >
                <X className="w-3 h-3" /> Clear
            </button>
        </div>
    );
}

export default function DeploymentReadinessBanner({ onVerdictResolved }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // iter84.57.10 — Decode the diagnostic snapshot from ?diag= once on
    // mount. Stored in state so the Clear button can wipe the ribbon
    // without forcing a full page reload (we replaceState the URL and
    // setSnapshot(null) in lockstep). Memoised so a re-render doesn't
    // re-decode on every paint.
    const [snapshot, setSnapshot] = useState(() => decodeDiagSnapshot(
        typeof window !== "undefined" ? window.location.search : "",
    ));
    const snapshotKeyMap = useMemo(() => {
        if (!snapshot) return null;
        const m = new Map();
        for (const f of snapshot.fails) if (f.key) m.set(f.key, "fail");
        for (const w of snapshot.warns) if (w.key) m.set(w.key, "warn");
        return m;
    }, [snapshot]);

    const fetchReadiness = useCallback(async (manual = false) => {
        if (manual) setRefreshing(true);
        try {
            const r = await api.getDeployReadiness();
            setData(r);
            if (manual) {
                if (r.overall === "ok") toast.success("All deployment checks passed");
                else if (r.overall === "warn") toast.warning(`${r.warn_count} warning(s) to review`);
                else toast.error(`${r.fail_count} blocker(s) — fix before deploying`);
            }
            // iter84.56.7-f — Hand the verdict back to the parent so it
            // can auto-tick the readiness gate. Parent only flips
            // pending gates, never overwriting operator decisions.
            if (onVerdictResolved) {
                onVerdictResolved({
                    overall: r.overall,
                    warn_count: r.warn_count || 0,
                    fail_count: r.fail_count || 0,
                });
            }
        } catch (err) {
            const msg = err.response?.data?.detail || "Failed to load readiness checks";
            toast.error(msg);
            setData({ overall: "fail", fail_count: 1, warn_count: 0, checks: [{ name: "Endpoint", status: "fail", detail: msg }] });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [onVerdictResolved]);

    useEffect(() => { fetchReadiness(false); }, [fetchReadiness]);

    if (loading) {
        return (
            <CollapsibleCard
                testId="deploy-readiness-banner-loading"
                icon={Rocket}
                title="Deployment readiness"
                badge="checking…"
                badgeClass="bg-zinc-700/30 text-zinc-300 border-zinc-700"
                verdict="info"
            >
                <p className="text-xs text-zinc-500 flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Running pre-deploy checks…
                </p>
            </CollapsibleCard>
        );
    }

    const overall = data?.overall || "warn";
    const meta = STATUS_META[overall] || STATUS_META.warn;
    const { Icon } = meta;

    // iter82.3 — Environment-aware copy. On preview this banner asks
    // "are we ready to deploy?"; on production it asks "did the deploy
    // land healthy?". Same checks, different framing.
    const isProd = data?.environment === "production";
    const title = isProd ? "Production health" : "Deployment readiness";
    const headline = overall === "ok"
        ? (isProd ? "Production is healthy" : "Preview is ready to deploy")
        : overall === "warn"
            ? (isProd
                ? `Production has ${data?.warn_count || 0} warning${data?.warn_count === 1 ? "" : "s"} — review`
                : `Deploy with caution — ${data?.warn_count || 0} warning${data?.warn_count === 1 ? "" : "s"}`)
            : (isProd
                ? `Production has ${data?.fail_count || 0} blocker${data?.fail_count === 1 ? "" : "s"} — investigate`
                : `Do not deploy — ${data?.fail_count || 0} blocker${data?.fail_count === 1 ? "" : "s"}`);

    const badge = `${meta.label}${data?.checks ? ` · ${data.checks.length}` : ""}`;
    const badgeClass = VERDICT_PILL[overall] || VERDICT_PILL.warn;

    const refreshBtn = (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); fetchReadiness(true); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); fetchReadiness(true); } }}
            data-testid="deploy-readiness-refresh-btn"
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
        >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Checking…" : "Re-check"}
        </span>
    );

    // iter84.57.2 — "Copy diagnostic snapshot" button. Serializes the
    // current readiness state into a base64 JSON payload + a ?diag=…
    // URL the operator can paste into the chat. Cheaper than a screenshot
    // and includes the iter id so the agent skips exploration steps.
    const copyDiagnostic = async (e) => {
        e?.stopPropagation();
        if (!data) {
            toast.error("Nothing to copy — readiness data not loaded yet.");
            return;
        }
        const payload = {
            ts: new Date().toISOString(),
            environment: data.environment || "unknown",
            overall: data.overall,
            iter: (data.checks || [])
                .find((c) => c.key === "agent_preflight")?.name
                ?.match(/iter([\d.]+)/)?.[1] || null,
            warns: (data.checks || [])
                .filter((c) => c.status === "warn")
                .map((c) => ({ key: c.key, name: c.name, detail: c.detail })),
            fails: (data.checks || [])
                .filter((c) => c.status === "fail")
                .map((c) => ({ key: c.key, name: c.name, detail: c.detail })),
        };
        const json = JSON.stringify(payload);
        const b64 = btoa(unescape(encodeURIComponent(json)))
            .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        const url = `${window.location.origin}/platform?diag=${b64}`;
        try {
            await navigator.clipboard.writeText(url);
            const stateLabel = payload.fails.length
                ? `${payload.fails.length} fail · ${payload.warns.length} warn`
                : payload.warns.length
                    ? `${payload.warns.length} warn`
                    : "all clear";
            toast.success(`Diagnostic snapshot copied · ${stateLabel}`);
        } catch (_err) {
            window.prompt("Copy this diagnostic URL:", url);
        }
    };

    const headerExtra = (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <WhatsThisButton
                testId="deploy-readiness-whats-this"
                title="Deployment Readiness"
                what="A pre-flight checklist that the app will actually RUN after you deploy: database connection, secret keys, admin accounts, email service, GitHub token expiry."
                lookFor="Every row green before you hit deploy. A red row is a config/environment problem — fix it in settings or .env, not in code."
                notFor="Game data quality (that's the Pre-deploy sweep) or whether the deploy worked (that's Post-Deploy Auto-Verify)."
            />
            {data && (
                <span
                    role="button"
                    tabIndex={0}
                    onClick={copyDiagnostic}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") copyDiagnostic(e); }}
                    data-testid="deploy-readiness-copy-diagnostic-btn"
                    className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
                    title="Copy a compact diagnostic URL of the current readiness state — paste into the chat to skip screenshots and exploration."
                >
                    <ClipboardCopy className="w-3 h-3" /> Copy diagnostic
                </span>
            )}
            {refreshBtn}
        </div>
    );

    return (
        <TooltipProvider delayDuration={150} skipDelayDuration={300}>
        <CollapsibleCard
            testId="deploy-readiness-banner"
            icon={Rocket}
            title={title}
            badge={badge}
            badgeClass={badgeClass}
            verdict={overallToVerdict(overall)}
            headerExtra={headerExtra}
            defaultOpen={!!snapshot}
        >
            <div data-status={overall}>
                {snapshot && (
                    <SnapshotRibbon
                        snapshot={snapshot}
                        liveOverall={data?.overall}
                        liveEnvironment={data?.environment}
                        liveIter={(data?.checks || []).find((c) => c.key === "agent_preflight")?.name?.match(/iter([\d.]+)/)?.[1] || null}
                        onClear={() => { clearDiagFromUrl(); setSnapshot(null); }}
                    />
                )}
                <p className={`text-sm font-bold mb-3 ${meta.color}`} data-testid="deploy-readiness-headline">
                    <Icon className="inline w-4 h-4 mr-1 -mt-0.5" />
                    {headline}
                </p>
                {(() => {
                    const allChecks = data?.checks || [];
                    const categories = data?.categories || [];
                    // iter84.56.10 — Group checks under their Protection
                    // Category. Falls back to a flat list if the backend
                    // hasn't been updated (graceful degradation for the
                    // first deploy of this iter, before any prod refresh).
                    if (!categories.length) {
                        return (
                            <ul className="divide-y divide-[#222]" data-testid="deploy-readiness-checks-list">
                                {allChecks.map((c) => (
                                    <CheckRow
                                        key={c.name}
                                        check={c}
                                        inSnapshot={!!(snapshotKeyMap && c.key && snapshotKeyMap.has(c.key))}
                                        snapshotStatus={snapshotKeyMap && c.key ? snapshotKeyMap.get(c.key) : null}
                                    />
                                ))}
                            </ul>
                        );
                    }
                    const groups = categories
                        .map((cat) => ({
                            ...cat,
                            checks: allChecks.filter((c) => c.category === cat.id),
                        }))
                        .filter((g) => g.checks.length > 0);
                    const unassigned = allChecks.filter((c) => !c.category);
                    return (
                        <div data-testid="deploy-readiness-checks-list" className="space-y-3">
                            {groups.map((g) => {
                                const crit = CRITICALITY_META[g.criticality] || CRITICALITY_META.operational;
                                return (
                                    <section
                                        key={g.id}
                                        data-testid={`deploy-readiness-category-${g.id}`}
                                        data-criticality={g.criticality}
                                    >
                                        <header className="flex items-start gap-2 pb-1.5 mb-1 border-b border-[#222]">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold uppercase tracking-wider text-[10px] text-white">
                                                        {g.title}
                                                    </span>
                                                    <span
                                                        className={`pill border text-[9px] font-bold uppercase py-0.5 px-1.5 ${crit.color} ${crit.border}`}
                                                        title={`Criticality: ${g.criticality}`}
                                                    >
                                                        {crit.label}
                                                    </span>
                                                </div>
                                                {g.tagline && (
                                                    <p className="text-[10px] text-zinc-500 italic mt-0.5 leading-snug">
                                                        {g.tagline}
                                                    </p>
                                                )}
                                            </div>
                                        </header>
                                        <ul className="divide-y divide-[#222]" data-testid={`deploy-readiness-checks-${g.id}`}>
                                            {g.checks.map((c) => (
                                                <CheckRow
                                                    key={c.name}
                                                    check={c}
                                                    inSnapshot={!!(snapshotKeyMap && c.key && snapshotKeyMap.has(c.key))}
                                                    snapshotStatus={snapshotKeyMap && c.key ? snapshotKeyMap.get(c.key) : null}
                                                />
                                            ))}
                                        </ul>
                                    </section>
                                );
                            })}
                            {unassigned.length > 0 && (
                                <section data-testid="deploy-readiness-category-unassigned">
                                    <header className="flex items-center gap-2 pb-1.5 mb-1 border-b border-[#222]">
                                        <span className="font-bold uppercase tracking-wider text-[10px] text-zinc-400">
                                            Other
                                        </span>
                                        <span className="text-[10px] text-zinc-500 italic">
                                            Signals not yet assigned to a protection category.
                                        </span>
                                    </header>
                                    <ul className="divide-y divide-[#222]">
                                        {unassigned.map((c) => (
                                            <CheckRow
                                                key={c.name}
                                                check={c}
                                                inSnapshot={!!(snapshotKeyMap && c.key && snapshotKeyMap.has(c.key))}
                                                snapshotStatus={snapshotKeyMap && c.key ? snapshotKeyMap.get(c.key) : null}
                                            />
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>
                    );
                })()}
            </div>
        </CollapsibleCard>
        </TooltipProvider>
    );
}
